import { computed, ref, type Ref } from 'vue'
import { useEquipes } from '~/composables/equipe/useEquipe'
import { useConcurrentForm } from '~/composables/orga/useConcurrentForm'
import { bindCreateEquipeForm } from '~/composables/equipe/createEquipeFormActions'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { useFormValidationFeedback } from '~/composables/ui/useFormValidationFeedback'
import { useFormDirtySnapshot } from '~/composables/ui/useFormDirtySnapshot'
import { useOrgEditActor } from '~/composables/auth/useOrgEditActor'
import { actorMayModifyEquipe } from '~~/utils/auth/orgEditAffordances'

export type EquipeContactRow = { conLib: string; conChamp: string }

export type CreateEquipeFormProps = {
  showOverlay: boolean
  directionName: string
  domaineName: string
  agenceName: string
  equipeToEdit: string
  isEditing: boolean
  defaultEquLib: string
}

type EquipeRoutePayload = {
  dirLib: string
  domLib: string
  ageLib: string
  equLib: string
}

type CreateEquipeFormEmit = {
  (event: 'close' | 'refetch'): void
  (event: 'created' | 'deleted', payload: EquipeRoutePayload): void
}

type EquipeDraft = EquipeRoutePayload & {
  equDesc: string
  equMis: string
  contacts: EquipeContactRow[]
}

function equipeContextLabelOf(props: CreateEquipeFormProps) {
  return computed(() => {
    const directionName = props.directionName
    const domaineName = props.domaineName
    const agenceName = props.agenceName
    if (directionName === domaineName && domaineName === agenceName) {
      return {
        kind: 'direction' as const,
        name: directionName,
        creation: "Création du CODIR pour la direction",
        modification: "Modification de l'équipe de CODIR pour la direction",
      }
    }
    if (domaineName === agenceName) {
      return {
        kind: 'domaine' as const,
        name: domaineName,
        creation: "Création du CODIR pour le domaine",
        modification: "Modification de l'équipe de CODIR pour le domaine",
      }
    }
    return {
      kind: 'agence' as const,
      name: agenceName,
      creation: "Création du CODIR pour l'agence",
      modification: "Modification de l'équipe de CODIR pour l'agence",
    }
  })
}

export function useCreateEquipeForm(
  props: CreateEquipeFormProps,
  emit: CreateEquipeFormEmit,
  hasUnsavedChanges: Ref<boolean>,
) {
  const { actor } = useOrgEditActor()
  const mayDeleteEquipe = computed(() =>
    actorMayModifyEquipe(actor.value, props.directionName, props.domaineName, props.agenceName),
  )

  const { storeEquipe, patchEquipe, fetchEquipeById, deleteEquipe } = useEquipes()
  const concurrent = useConcurrentForm()
  const toast = useSourceToast()
  const feedback = useFormValidationFeedback({
    fieldOrder: ['equLib', 'equDesc', 'equMis'],
  })

  const loading = ref(false)
  const loadDone = ref(false)
  const showDeleteConfirm = ref(false)
  const equipeKeyToPatch = ref('')
  const contactsTableRef = ref<{ hideUndoBanner: () => void } | null>(null)

  const equipe = ref<EquipeDraft>({
    dirLib: props.directionName,
    domLib: props.domaineName,
    ageLib: props.agenceName,
    equLib: '',
    equDesc: '',
    equMis: '',
    contacts: [],
  })

  const showOverlayRef = computed(() => props.showOverlay)

  const { captureSnapshot } = useFormDirtySnapshot({
    showOverlay: showOverlayRef,
    currentValue: equipe,
    hasUnsavedChanges,
  })

  const equipeContextLabel = equipeContextLabelOf(props)

  const effectiveDefaultEquLib = computed(() =>
    props.defaultEquLib || (equipeContextLabel.value.kind === 'domaine' ? props.domaineName : '')
  )

  const actions = bindCreateEquipeForm({
    props,
    emit,
    hasUnsavedChanges,
    storeEquipe,
    patchEquipe,
    fetchEquipeById,
    deleteEquipe,
    toast,
    feedback,
    loading,
    loadDone,
    showDeleteConfirm,
    equipeKeyToPatch,
    contactsTableRef,
    equipe,
    captureSnapshot,
    effectiveDefaultEquLib,
    concurrent,
  })

  return {
    mayDeleteEquipe,
    feedback,
    loading,
    loadDone,
    showDeleteConfirm,
    contactsTableRef,
    equipe,
    equipeContextLabel,
    hideEquipeNameField: actions.hideEquipeNameField,
    showDiscardConfirm: actions.showDiscardConfirm,
    requestClose: actions.requestClose,
    confirmDiscard: actions.confirmDiscard,
    cancelDiscard: actions.cancelDiscard,
    validateForm: actions.validateForm,
    onConfirmDelete: actions.onConfirmDelete,
    concurrent,
  }
}
